/**
 * Instant quote calculator for the 1K · Quote section.
 *
 * Pricing data comes from snippets/kp-quote-pricing.liquid (rendered as JSON inside the element).
 * Step 05 can be the built-in request form (snippets/kp-quote-request-form.liquid): on send, the
 * design is uploaded to Cloudinary, the estimate is copied into hidden fields, and the form is
 * submitted to Shopify's contact endpoint, which emails the store.
 *
 * The calculation matches the original instant quote form:
 *   unit price (by method, quantity tier, garment and layout group) x quantity = subtotal,
 *   subtotal x HST rate = HST, subtotal + HST = total.
 * Shipping across Canada is 50% off or free above the subtotals in the pricing data's "shipping",
 * and a nudge offers the next quantity that reaches the next level. Items marked "quote_only"
 * (hoodies, totes, anything else) have no online price and are sent with the customer's description.
 */
class KpQuote extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('script[type="application/json"]:not([data-colours])');
    if (!dataEl) return;

    try {
      this.data = JSON.parse(dataEl.textContent);
    } catch (error) {
      console.error('kp-quote: pricing data is not valid JSON', error);
      return;
    }

    // Shirt colour lists for the colour picker; without them colours are typed in
    try {
      this.colours = JSON.parse(this.querySelector('script[data-colours]')?.textContent || '{}');
    } catch (error) {
      console.error('kp-quote: colour data is not valid JSON', error);
      this.colours = {};
    }

    this.refs = {
      methods: this.querySelector('[data-ref="methods"]'),
      layout: this.querySelector('[data-ref="layout"]'),
      garment: this.querySelector('[data-ref="garment"]'),
      garmentNote: this.querySelector('[data-ref="garment-note"]'),
      quantity: this.querySelector('[data-ref="quantity"]'),
      bulkWrap: this.querySelector('[data-ref="bulk-wrap"]'),
      bulkCount: this.querySelector('[data-ref="bulk-count"]'),
      sizeRows: this.querySelector('[data-ref="size-rows"]'),
      sizeTemplate: this.querySelector('[data-ref="size-row-template"]'),
      sizesTotal: this.querySelector('[data-ref="sizes-total"]'),
      addColour: this.querySelector('[data-ref="add-colour"]'),
      summaryPanel: this.querySelector('.kp-quote__summary'),
      steps: this.querySelector('.kp-quote__form'),
      unit: this.querySelector('[data-ref="unit"]'),
      discount: this.querySelector('[data-ref="discount"]'),
      subtotal: this.querySelector('[data-ref="subtotal"]'),
      hst: this.querySelector('[data-ref="hst"]'),
      total: this.querySelector('[data-ref="total"]'),
      result: this.querySelector('[data-ref="result"]'),
      unavailable: this.querySelector('[data-ref="unavailable"]'),
      copyWrap: this.querySelector('[data-ref="copy-wrap"]'),
      copy: this.querySelector('[data-ref="copy"]'),
      copyStatus: this.querySelector('[data-ref="copy-status"]'),
      details: this.querySelector('[data-ref="details"]'),
      bar: this.querySelector('[data-ref="bar"]'),
      barTotal: this.querySelector('[data-ref="bar-total"]'),
      barCopy: this.querySelector('[data-ref="bar-copy"]'),
      choices: this.querySelector('[data-ref="choices"]'),
      choiceMethod: this.querySelector('[data-ref="choice-method"]'),
      choiceLayout: this.querySelector('[data-ref="choice-layout"]'),
      choiceGarment: this.querySelector('[data-ref="choice-garment"]'),
      choiceQuantity: this.querySelector('[data-ref="choice-quantity"]'),
      choiceFileRow: this.querySelector('[data-ref="choice-file-row"]'),
      choiceFile: this.querySelector('[data-ref="choice-file"]'),
      itemDetailsWrap: this.querySelector('[data-ref="item-details-wrap"]'),
      itemDetails: this.querySelector('[data-ref="item-details"]'),
      shipping: this.querySelector('[data-ref="shipping"]'),
      shippingValue: this.querySelector('[data-ref="shipping-value"]'),
      shippingNudgeText: this.querySelector('[data-ref="shipping-nudge-text"]'),
      shippingUpgrade: this.querySelector('[data-ref="shipping-upgrade"]'),
      requestForm: this.querySelector('form.kp-request'),
      fileInput: this.querySelector('[data-ref="file"]'),
      progress: this.querySelector('[data-ref="progress"]'),
      submit: this.querySelector('[data-ref="submit"]'),
    };

    const firstMethod = this.data.methods[0];
    this.state = {
      method: firstMethod.id,
      layout: firstMethod.layouts[0],
      garment: this.data.default_garment || this.data.garments[0].id,
      quantity: firstMethod.default_quantity || firstMethod.quantities[0],
      // "More than <largest>": not priced online, we quote it
      bulk: false,
    };
    this.applyLinkChoices();

    this.abort = new AbortController();
    const { signal } = this.abort;

    this.refs.layout?.addEventListener('change', (event) => {
      this.state.layout = event.target.value;
      this.update();
    }, { signal });

    this.refs.garment?.addEventListener('change', (event) => {
      this.state.garment = event.target.value;
      this.renderQuantities();
      this.renderColourOptions();
      this.update();
    }, { signal });

    this.refs.quantity?.addEventListener('change', (event) => {
      const value = event.target.value;
      this.state.bulk = value === 'more';
      if (!this.state.bulk) this.state.quantity = parseInt(value, 10);
      this.update();
    }, { signal });

    this.refs.bulkCount?.addEventListener('input', () => this.showChoices(), { signal });

    this.refs.addColour?.addEventListener('click', () => {
      const row = this.addSizeRow();
      row?.querySelector('select:not([hidden]), input[data-colour-text]:not([hidden])')?.focus();
    }, { signal });
    this.refs.sizeRows?.addEventListener('input', (event) => this.onSizesInput(event), { signal });
    this.refs.sizeRows?.addEventListener('change', (event) => this.onSizesInput(event), { signal });
    this.refs.sizeRows?.addEventListener('click', (event) => this.onSizesClick(event), { signal });
    this.addSizeRow();
    this.applyLinkColour();

    this.refs.shippingUpgrade?.addEventListener('click', () => this.upgradeQuantity(), { signal });

    this.refs.copy?.addEventListener('click', () => this.copySummary(), { signal });
    this.refs.barCopy?.addEventListener('click', () => this.copySummary(), { signal });

    // Built-in request form: everything is sent in one email, so copying isn't needed
    if (this.refs.requestForm) {
      // Capture on window so this runs before Shopify's spam protection, which can send the form itself
      window.addEventListener(
        'submit',
        (event) => {
          if (event.target === this.refs.requestForm) this.onRequestSubmit(event);
        },
        { capture: true, signal }
      );
      this.refs.fileInput?.addEventListener('change', () => this.onFileChange(), { signal });
      // Back button after sending: the browser can restore this page with the button still "Sending…"
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.resetSending();
      }, { signal });
      this.refs.requestForm.addEventListener('change', (event) => {
        if (event.target instanceof HTMLInputElement && event.target.name === 'contact[Reply by]') this.updateReplyFields();
      }, { signal });
      this.updateReplyFields();
      this.querySelector('[data-ref="copy-wrap"]')?.remove();
      this.refs.copyWrap = null;
      this.refs.barCopy?.remove();
      this.refs.barCopy = null;
    }

    // Phones: the full estimate is at the bottom, so a slim bar shows the total while the steps
    // (01-05) are on screen, and hides once the full estimate comes into view
    if (this.refs.bar && 'IntersectionObserver' in window) {
      this.inView = new Set();
      this.observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.inView.add(entry.target);
          else this.inView.delete(entry.target);
        }
        this.updateBar();
      });
      for (const element of [this.refs.steps, this.refs.details, this.refs.summaryPanel]) {
        if (element) this.observer.observe(element);
      }
    }

    this.renderAll();
    this.hidden = false;
    this.showThanksIfSent();
    this.querySelector('[data-ref="nojs"]')?.remove();
  }

  /**
   * Links can pre-pick the print method and item, e.g. /pages/custom-quote?method=DTF or ?item=hoodie
   * (from the service pages). Matches a method or item id, or part of it ("5000", "hoodie", "tote").
   */
  applyLinkChoices() {
    this.restoreChoices();

    const params = new URLSearchParams(window.location.search);
    const find = (list, value) => {
      const wanted = value?.trim().toLowerCase();
      if (!wanted) return undefined;
      return (
        list.find((entry) => entry.id.toLowerCase() === wanted) ||
        list.find((entry) => entry.id.toLowerCase().includes(wanted) || entry.label.toLowerCase().includes(wanted))
      );
    };

    const method = find(this.data.methods, params.get('method'));
    if (method && method.id !== this.state.method) {
      this.state.method = method.id;
      this.state.layout = method.layouts[0];
      this.state.quantity = method.default_quantity || method.quantities[0];
      this.state.bulk = false;
    }
    const garment = find(this.data.garments, params.get('item'));
    if (garment) this.state.garment = garment.id;

    // ?layout=chest picks the first design layout of the chosen method that mentions it
    const layoutWanted = params.get('layout')?.trim().toLowerCase();
    if (layoutWanted) {
      const layout = this.method.layouts.find((entry) => entry.toLowerCase().includes(layoutWanted));
      if (layout) this.state.layout = layout;
    }

    // ?qty=40 picks the closest quantity option at or above it; above the largest is "More than"
    const qty = parseInt(params.get('qty') || params.get('quantity') || '', 10);
    if (qty > 0) {
      const quantities = this.method.quantities;
      const option = quantities.find((value) => value >= qty);
      this.state.bulk = !option;
      if (option) this.state.quantity = option;
    }

    // ?colour=Black is applied to the first colour row once the rows exist (see applyLinkColour)
    this.linkColour = (params.get('colour') || params.get('color') || '').trim();
  }

  /** Picks the colour from the link in the first colour row, if the chosen shirt has it */
  applyLinkColour() {
    if (!this.linkColour) return;
    const select = this.refs.sizeRows?.querySelector('[data-colour-select]');
    const text = this.refs.sizeRows?.querySelector('[data-colour-text]');
    const match = this.colourList?.find((colour) => colour.name.toLowerCase() === this.linkColour.toLowerCase());
    if (match && select instanceof HTMLSelectElement) {
      select.value = match.name;
      const row = select.closest('.kp-quote__size-row');
      if (row) this.updateSwatch(row);
    } else if (!this.colourList && text instanceof HTMLInputElement) {
      text.value = this.linkColour;
    }
    this.updateSizes();
  }

  /**
   * Remembers steps 01-04 for this visit, so leaving to look at shirt colours and coming back
   * keeps the customer's choices. Links (?item=, ?method=, ?qty=) still win over saved choices.
   */
  saveChoices() {
    try {
      const { method, layout, garment, quantity, bulk } = this.state;
      sessionStorage.setItem('kp-quote-choices', JSON.stringify({ method, layout, garment, quantity, bulk }));
    } catch {
      // Storage blocked: choices just aren't remembered
    }
  }

  restoreChoices() {
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem('kp-quote-choices') || 'null');
    } catch {
      saved = null;
    }
    if (!saved) return;
    const method = this.data.methods.find((entry) => entry.id === saved.method);
    if (!method) return;
    this.state.method = method.id;
    this.state.layout = method.layouts.includes(saved.layout) ? saved.layout : method.layouts[0];
    this.state.quantity = method.quantities.includes(saved.quantity)
      ? saved.quantity
      : method.default_quantity || method.quantities[0];
    this.state.bulk = Boolean(saved.bulk);
    if (this.data.garments.some((entry) => entry.id === saved.garment)) this.state.garment = saved.garment;
  }

  /**
   * After a request is sent, Shopify reloads the page with ?contact_posted=true (and the form
   * renders its success message). Swap the form for the thank-you screen.
   */
  showThanksIfSent() {
    const sent =
      new URLSearchParams(window.location.search).get('contact_posted') === 'true' ||
      Boolean(this.querySelector('[data-kp-posted]'));
    if (!sent) return;
    const thanks = this.parentElement?.querySelector('[data-kp-thanks]');
    if (!(thanks instanceof HTMLElement)) return;
    this.showReply(thanks);
    thanks.hidden = false;
    this.hidden = true;
    this.refs.bar?.classList.remove('is-visible');
    // Shopify adds #form-id to the address, which makes the browser jump to the (now hidden) form
    if (window.location.hash) {
      history.replaceState(history.state, '', window.location.pathname + window.location.search);
    }
    // Bring the message just under the header. Again once the page has loaded: images above it
    // (the page header photo) can push it down, and the browser may restore its own scroll
    const scrollToThanks = () => {
      const header = document.querySelector('header-component');
      const offset = (header?.getBoundingClientRect().height || 64) + 16;
      const top = thanks.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'instant' });
    };
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    requestAnimationFrame(() => {
      scrollToThanks();
      thanks.focus({ preventScroll: true });
    });
    if (document.readyState === 'complete') setTimeout(scrollToThanks, 150);
    else window.addEventListener('load', () => setTimeout(scrollToThanks, 50), { once: true });
  }

  /** True when the customer wants their quote by text instead of email */
  get replyByText() {
    return this.refs.requestForm?.querySelector('input[name="contact[Reply by]"]:checked')?.value === 'Text';
  }

  /** Only the contact field for the chosen reply method is required (the other one is hidden by CSS) */
  updateReplyFields() {
    const form = this.refs.requestForm;
    const email = form?.querySelector('[name="contact[email]"]');
    const phone = form?.querySelector('[name="contact[phone]"]');
    if (email instanceof HTMLInputElement) email.required = !this.replyByText;
    if (phone instanceof HTMLInputElement) phone.required = this.replyByText;
  }

  updateBar() {
    if (!this.refs.bar || !this.inView) return;
    const stepsInView = this.inView.has(this.refs.steps) || this.inView.has(this.refs.details);
    const summaryInView = this.inView.has(this.refs.summaryPanel);
    this.refs.bar.classList.toggle('is-visible', stepsInView && !summaryInView && Boolean(this.quote));
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
    clearTimeout(this.copyTimer);
    clearTimeout(this.sendTimer);
  }

  get method() {
    return this.data.methods.find((method) => method.id === this.state.method);
  }

  /**
   * Sample orders from the pricing data, each with its price per shirt and subtotal. Pressing one
   * fills in steps 01-04 with it.
   */
  renderExamples() {
    const list = this.querySelector('[data-ref="examples"]');
    const wrap = this.querySelector('[data-ref="examples-wrap"]');
    if (!list || !wrap || this.examplesRendered) return;
    const items = (this.data.examples || [])
      .map((example) => {
        const method = this.data.methods.find((entry) => entry.id === example.method);
        if (!method || !method.layouts.includes(example.layout)) return null;
        const tier = method.tiers.find((entry) => example.quantity >= entry.min)?.name;
        const group = this.getLayoutGroup(example.layout, method.id);
        const row = this.data.prices.find(
          (price) => price.method === method.id && price.tier === tier && price.garment === example.garment && price.layout === group
        );
        return row ? { example, unit: row.retail } : null;
      })
      .filter(Boolean);

    list.replaceChildren(
      ...items.map(({ example, unit }) => {
        const item = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'kp-quote__example';
        const label = document.createElement('span');
        label.textContent = example.label;
        const price = document.createElement('span');
        price.className = 'kp-quote__example-price';
        price.textContent = `${this.money(unit)} each · ${this.money(unit * example.quantity)} + HST`;
        button.append(label, price);
        button.setAttribute('aria-label', `${example.label}: ${price.textContent}. Use this order in the calculator`);
        button.addEventListener('click', () => this.useExample(example));
        item.append(button);
        return item;
      })
    );
    wrap.hidden = !items.length;
    this.examplesRendered = true;
  }

  /** @param {{ method: string, garment: string, layout: string, quantity: number }} example */
  useExample(example) {
    this.state.method = example.method;
    this.state.layout = example.layout;
    this.state.garment = example.garment;
    this.state.quantity = example.quantity;
    this.state.bulk = false;
    this.renderAll();
    this.renderColourOptions();
    // Phones: the estimate sits below the form, so bring the filled-in steps back into view
    if (window.matchMedia('(max-width: 989px)').matches) {
      this.refs.steps?.scrollIntoView({ block: 'start' });
    }
    this.querySelector('.kp-quote__method[aria-pressed="true"]')?.focus({ preventScroll: true });
  }

  renderAll() {
    this.renderExamples();
    this.renderMethods();
    this.renderSelect(this.refs.layout, this.method.layouts.map((layout) => ({ id: layout, label: layout })), this.state.layout);
    this.renderGarments();
    this.renderQuantities();
    this.update();
  }

  get garment() {
    return this.data.garments.find((item) => item.id === this.state.garment);
  }

  /** Shirts are priced online; anything else is quoted, so it counts in pieces */
  get unitNoun() {
    return this.garment?.quote_only ? 'pieces' : 'shirts';
  }

  renderQuantities() {
    const quantities = this.method.quantities;
    const largest = quantities[quantities.length - 1];
    this.renderSelect(
      this.refs.quantity,
      [
        ...quantities.map((quantity) => ({ id: String(quantity), label: `${quantity} ${this.unitNoun}` })),
        { id: 'more', label: `More than ${largest} ${this.unitNoun}` },
      ],
      this.state.bulk ? 'more' : String(this.state.quantity)
    );
  }

  /** Quantity as words for the summary and email, e.g. "20 shirts" or "More than 200 (about 300)" */
  get quantityText() {
    if (!this.state.bulk) return `${this.state.quantity} ${this.unitNoun}`;
    const largest = this.method.quantities[this.method.quantities.length - 1];
    const count = parseInt(this.refs.bulkCount?.value || '', 10);
    return `More than ${largest} ${this.unitNoun}${count > 0 ? ` (about ${count})` : ''}`;
  }

  /** The colour list for the chosen item, or null when colours are typed in */
  get colourList() {
    const key = this.garment?.colours;
    const list = key ? this.colours?.[key] : null;
    return Array.isArray(list) && list.length ? list : null;
  }

  /** Adds a colour row (colour + how many of each size) and returns it */
  addSizeRow() {
    const container = this.refs.sizeRows;
    const template = this.refs.sizeTemplate;
    if (!container || !(template instanceof HTMLTemplateElement)) return null;
    const row = template.content.firstElementChild?.cloneNode(true);
    if (!(row instanceof HTMLElement)) return null;
    container.append(row);
    this.renderColourOptions();
    this.updateSizes();
    return row;
  }

  /** Fills each row's colour dropdown for the chosen item, keeping colours already picked */
  renderColourOptions() {
    const list = this.colourList;
    const rows = [...(this.refs.sizeRows?.children || [])];
    rows.forEach((row, index) => {
      const select = row.querySelector('[data-colour-select]');
      const text = row.querySelector('[data-colour-text]');
      const remove = row.querySelector('[data-remove]');
      row.setAttribute('aria-label', `Colour ${index + 1}`);
      if (remove instanceof HTMLElement) remove.hidden = rows.length < 2;
      if (!(select instanceof HTMLSelectElement) || !(text instanceof HTMLInputElement)) return;

      select.hidden = !list;
      text.hidden = Boolean(list);
      // Typed colours: start from the colour picked in the list, if any
      if (!list && !text.value.trim() && select.value) text.value = select.value;
      if (list) {
        const current = select.value || text.value.trim();
        // Popular colours first so most people don't scroll the full list
        const names = list.map((colour) => colour.name);
        const popular = (this.garment?.popular || []).filter((name) => names.includes(name));
        const group = (label, items) => {
          const element = document.createElement('optgroup');
          element.label = label;
          element.append(...items.map((name) => new Option(name, name)));
          return element;
        };
        const placeholder = new Option('Choose a colour', '');
        if (popular.length) {
          const rest = names.filter((name) => !popular.includes(name));
          select.replaceChildren(placeholder, group('Popular colours', popular), group('All colours', rest));
        } else {
          select.replaceChildren(placeholder, ...names.map((name) => new Option(name, name)));
        }
        select.value = list.some((colour) => colour.name === current) ? current : '';
      }
      this.updateSwatch(row);
    });
  }

  /** @param {Element} row */
  updateSwatch(row) {
    const swatch = row.querySelector('[data-swatch]');
    const select = row.querySelector('[data-colour-select]');
    if (!(swatch instanceof HTMLImageElement)) return;
    const colour = select instanceof HTMLSelectElement && !select.hidden
      ? this.colourList?.find((item) => item.name === select.value)
      : null;
    swatch.hidden = !colour;
    if (colour) swatch.src = colour.swatch;
  }

  /** @param {Event} event */
  onSizesInput(event) {
    const target = event.target;
    if (target instanceof HTMLSelectElement && target.matches('[data-colour-select]')) {
      const row = target.closest('.kp-quote__size-row');
      if (row) this.updateSwatch(row);
    }
    this.updateSizes();
  }

  /** @param {Event} event */
  onSizesClick(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-remove]') : null;
    if (!button) return;
    const row = button.closest('.kp-quote__size-row');
    const rows = [...(this.refs.sizeRows?.children || [])];
    const index = rows.indexOf(row);
    row?.remove();
    this.renderColourOptions();
    this.updateSizes();
    // Keep focus nearby: the previous row's colour, or "Add another colour"
    const next = this.refs.sizeRows?.children[Math.max(0, index - 1)];
    const field = next?.querySelector('select:not([hidden]), input[data-colour-text]:not([hidden])');
    if (field instanceof HTMLElement) field.focus();
    else this.refs.addColour?.focus();
  }

  /** Each colour row as { colour, sizes: [[size, count]], total } (rows left empty are skipped) */
  get sizeEntries() {
    const rows = [...(this.refs.sizeRows?.children || [])];
    return rows
      .map((row) => {
        const select = row.querySelector('[data-colour-select]');
        const text = row.querySelector('[data-colour-text]');
        const colour =
          select instanceof HTMLSelectElement && !select.hidden
            ? select.value
            : text instanceof HTMLInputElement
              ? text.value.trim()
              : '';
        const sizes = [...row.querySelectorAll('[data-size]')]
          .map((input) => [input.dataset.size, Math.max(0, parseInt(input.value, 10) || 0)])
          .filter(([, count]) => count > 0);
        const total = sizes.reduce((sum, [, count]) => sum + count, 0);
        return { colour, sizes, total };
      })
      .filter((entry) => entry.colour || entry.total);
  }

  /** Colours and sizes as text for the email, e.g. "Black: S 5, M 10 (15)" per line */
  get sizesText() {
    return this.sizeEntries
      .map((entry) => {
        const sizes = entry.sizes.map(([size, count]) => `${size} ${count}`).join(', ');
        const name = entry.colour || 'Colour not chosen';
        return sizes ? `${name}: ${sizes} (${entry.total})` : `${name}: sizes not given`;
      })
      .join('\n');
  }

  /** Shows how many shirts the sizes add up to, and whether that matches the quantity */
  updateSizes() {
    const total = this.sizeEntries.reduce((sum, entry) => sum + entry.total, 0);
    let text = '';
    if (total > 0) {
      text = `Sizes add up to ${total} ${this.unitNoun}`;
      if (!this.state.bulk && total !== this.state.quantity) {
        text += ` (quantity above: ${this.state.quantity})`;
      }
    }
    this.setText('sizesTotal', text);
  }

  /** Priced t-shirts first, then the items we quote by hand, each in its own group */
  renderGarments() {
    const select = this.refs.garment;
    if (!select) return;
    // The badge only shows in the dropdown; the summary and email use the plain label
    const withBadge = (item) => ({ ...item, label: item.badge ? `${item.label} (${item.badge})` : item.label });
    const priced = this.data.garments.filter((item) => !item.quote_only).map(withBadge);
    const quoted = this.data.garments.filter((item) => item.quote_only);
    if (!quoted.length) {
      this.renderSelect(select, priced, this.state.garment);
      return;
    }
    const group = (label, items) => {
      const element = document.createElement('optgroup');
      element.label = label;
      element.append(
        ...items.map((item) => {
          const option = document.createElement('option');
          option.value = item.id;
          option.textContent = item.label;
          option.selected = item.id === this.state.garment;
          return option;
        })
      );
      return element;
    };
    select.replaceChildren(group('T-shirts (priced online)', priced), group('Other items (we quote these)', quoted));
  }

  renderMethods() {
    const container = this.refs.methods;
    if (!container) return;
    container.replaceChildren();

    for (const method of this.data.methods) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'kp-quote__method';
      button.textContent = method.label;
      button.setAttribute('aria-pressed', String(method.id === this.state.method));
      button.addEventListener('click', () => this.selectMethod(method.id));
      container.append(button);
    }
  }

  selectMethod(methodId) {
    if (methodId === this.state.method) return;
    this.state.method = methodId;
    this.state.layout = this.method.layouts[0];

    // Each method's minimums carry over to each other: 10 DTF -> 15 screen, 15 screen -> 10 DTF
    if (methodId === 'Screen' && this.state.quantity === 10) this.state.quantity = 15;
    if (methodId === 'DTF' && this.state.quantity === 15) this.state.quantity = 10;
    if (!this.method.quantities.includes(this.state.quantity)) {
      this.state.quantity = this.method.default_quantity || this.method.quantities[0];
    }
    // "More than <largest>" stays chosen when switching methods

    this.renderAll();
    this.querySelector('.kp-quote__method[aria-pressed="true"]')?.focus();
  }

  /**
   * @param {HTMLSelectElement | null} select
   * @param {{ id: string, label: string }[]} options
   * @param {string} selected
   */
  renderSelect(select, options, selected) {
    if (!select) return;
    select.replaceChildren(
      ...options.map((option) => {
        const element = document.createElement('option');
        element.value = option.id;
        element.textContent = option.label;
        element.selected = option.id === selected;
        return element;
      })
    );
  }

  getTier(quantity) {
    return this.method.tiers.find((tier) => quantity >= tier.min)?.name;
  }

  /** Maps a customer-facing layout to its pricing group, exactly as the original form did. */
  getLayoutGroup(layout, method = this.state.method) {
    if (method === 'DTF') {
      if (layout.includes('2 Large Designs')) return '2 Large Designs';
      if (layout.includes('Neck Tag') || layout.includes('Left Chest')) return '1 Design + Small Print';
      return '1 Design';
    }
    return layout.includes('2 Designs') ? '2 Designs' : '1 Design';
  }

  findPrice(tier) {
    const layoutGroup = this.getLayoutGroup(this.state.layout);
    return this.data.prices.find(
      (row) =>
        row.method === this.state.method &&
        row.tier === tier &&
        row.garment === this.state.garment &&
        row.layout === layoutGroup
    );
  }

  update() {
    this.saveChoices();
    this.showChoices();
    const quoteOnly = Boolean(this.garment?.quote_only);
    if (this.refs.itemDetailsWrap) this.refs.itemDetailsWrap.hidden = !quoteOnly;
    if (quoteOnly && this.refs.itemDetails && this.garment?.placeholder) {
      this.refs.itemDetails.placeholder = this.garment.placeholder;
    }
    if (this.refs.garmentNote) {
      const note = this.garment?.note || '';
      this.refs.garmentNote.textContent = note;
      this.refs.garmentNote.hidden = !note;
    }
    if (this.refs.bulkWrap) this.refs.bulkWrap.hidden = !this.state.bulk;
    this.updateSizes();
    const tier = this.getTier(this.state.quantity);
    const price = quoteOnly || this.state.bulk ? null : this.findPrice(tier);

    if (!price) {
      this.quote = null;
      if (this.refs.result) this.refs.result.hidden = true;
      if (this.refs.unavailable) {
        this.refs.unavailable.hidden = false;
        let message = "This combination isn't priced online. Send us the details and we'll quote it for you.";
        if (quoteOnly) {
          message = `We price ${this.garment.quote_name || this.garment.label.toLowerCase()} by quote. Send us your details and we'll reply with your price.`;
        } else if (this.state.bulk) {
          message = "For orders this size we'll send you our best bulk price. Send us your details and we'll reply with your quote.";
        }
        this.refs.unavailable.textContent = message;
      }
      if (this.refs.shipping) this.refs.shipping.hidden = true;
      if (this.refs.copyWrap) this.refs.copyWrap.hidden = true;
      this.updateBar();
      return;
    }

    const unit = price.retail;
    const subtotal = unit * this.state.quantity;
    const hst = subtotal * this.data.hst_rate;
    const total = subtotal + hst;

    // Bulk discount: compare against the smallest-quantity tier for the same shirt and layout
    const baseTier = this.method.tiers[this.method.tiers.length - 1].name;
    const basePrice = this.findPrice(baseTier);
    const saving = basePrice ? basePrice.retail - unit : 0;

    this.quote = { tier, unit, subtotal, hst, total };

    this.setText('unit', this.money(unit));
    this.setText('subtotal', this.money(subtotal));
    this.setText('hst', this.money(hst));
    this.setText('total', this.money(total));

    if (this.refs.discount) {
      this.refs.discount.hidden = saving <= 0.001;
      this.refs.discount.textContent = `Bulk price: you save ${this.money(saving)} per shirt`;
    }

    this.updateShipping(subtotal);

    if (this.refs.result) this.refs.result.hidden = false;
    if (this.refs.unavailable) this.refs.unavailable.hidden = true;
    if (this.refs.copyWrap) this.refs.copyWrap.hidden = false;
    if (this.refs.barTotal) this.refs.barTotal.textContent = this.money(total);
    this.updateBar();
  }

  /** Subtotal for another quantity of the same item and layout, or null if it isn't priced */
  subtotalFor(quantity) {
    const tier = this.method.tiers.find((item) => quantity >= item.min)?.name;
    const price = this.findPrice(tier);
    return price ? price.retail * quantity : null;
  }

  /** Smallest quantity option above the current one whose subtotal reaches the amount */
  quantityFor(amount) {
    return this.method.quantities.find(
      (quantity) => quantity > this.state.quantity && Math.round(this.subtotalFor(quantity) ?? 0) >= amount
    );
  }

  /**
   * Shows the shipping level in the estimate and nudges towards the next one. Shipping isn't in the
   * total: its cost depends on where the order goes, so it's added to the final quote.
   * @param {number} subtotal
   */
  updateShipping(subtotal) {
    const box = this.refs.shipping;
    const offer = this.data.shipping;
    if (!box || !offer) return;
    // Rounded to the dollar, so prices like $199.80 count as $200
    const rounded = Math.round(subtotal);
    const half = Number(offer.half_off_min);
    const free = Number(offer.free_min);

    let value = 'Added to your quote';
    let label = 'Added to quote';
    if (rounded >= free) {
      value = 'Free';
      label = 'Free shipping across Canada';
    } else if (rounded >= half) {
      value = '50% off, added to your quote';
      label = '50% off shipping across Canada';
    }
    this.shippingLabel = label;

    const freeQuantity = rounded < free ? this.quantityFor(free) : undefined;
    const halfQuantity = rounded < half ? this.quantityFor(half) : undefined;
    const noun = this.unitNoun;
    const more = (quantity) => quantity - this.state.quantity;
    let nudge = '';
    let target;
    if (halfQuantity && freeQuantity && halfQuantity !== freeQuantity) {
      nudge = `Add ${more(halfQuantity)} more ${noun} for 50% off shipping, or ${more(freeQuantity)} more for free shipping.`;
      target = halfQuantity;
    } else if (freeQuantity) {
      nudge = `Add ${more(freeQuantity)} more ${noun} for free shipping.`;
      target = freeQuantity;
    } else if (halfQuantity) {
      nudge = `Add ${more(halfQuantity)} more ${noun} for 50% off shipping.`;
      target = halfQuantity;
    }

    this.upgradeTarget = target;
    this.setText('shippingValue', value);
    this.setText('shippingNudgeText', nudge);
    this.setText('shippingUpgrade', target ? `Switch to ${target} ${noun}` : '');
    box.hidden = !nudge;
  }

  /** The nudge's button: move to the quantity it suggested */
  upgradeQuantity() {
    if (!this.upgradeTarget) return;
    this.state.quantity = this.upgradeTarget;
    this.renderQuantities();
    this.update();
    this.refs.quantity?.focus();
  }

  /** Mirrors what the customer has picked into the estimate panel */
  showChoices() {
    const garment = this.garment?.label || this.state.garment;
    this.setText('choiceMethod', this.method.label);
    this.setText('choiceLayout', this.state.layout);
    this.setText('choiceGarment', garment);
    this.setText('choiceQuantity', this.quantityText);
    if (this.refs.choices) this.refs.choices.hidden = false;
  }

  setText(ref, text) {
    if (this.refs[ref]) this.refs[ref].textContent = text;
  }

  money(value) {
    return `$${value.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  get summary() {
    const lines = [
      this.quote ? 'Estimate' : 'Quote request (not priced online)',
      `Print method: ${this.method.label}`,
      `Design layout: ${this.state.layout}`,
      `Item: ${this.garment?.label || this.state.garment}`,
    ];
    if (this.itemDetails) lines.push(`Item details: ${this.itemDetails}`);
    lines.push(`Quantity: ${this.quantityText}`);
    if (this.sizesText) lines.push(`Colours and sizes:\n${this.sizesText}`);
    if (this.quote) {
      lines.push(
        `Unit price: ${this.money(this.quote.unit)}`,
        `Subtotal: ${this.money(this.quote.subtotal)}`,
        `HST (${Math.round(this.data.hst_rate * 100)}%): ${this.money(this.quote.hst)}`,
        `Total: ${this.money(this.quote.total)}`,
        `Shipping: ${this.shippingLabel || 'Added to quote'}`
      );
    }
    return lines.join('\n');
  }

  get itemDetails() {
    if (!this.garment?.quote_only) return '';
    return this.refs.itemDetails?.value.trim() || '';
  }

  /**
   * Uploads the design as soon as it's chosen, so its link is already in the form when the
   * customer presses send. Shopify's spam protection can submit the form on its own, so nothing
   * slow may happen at send time.
   */
  async onFileChange() {
    const input = this.refs.fileInput;
    const file = input?.files?.[0];
    if (this.refs.choiceFileRow) this.refs.choiceFileRow.hidden = !file;
    if (this.refs.choiceFile) this.refs.choiceFile.textContent = file ? file.name : '';
    this.setFileFields('', '');

    const token = (this.uploadToken = (this.uploadToken || 0) + 1);
    if (!file) {
      this.setProgress('');
      return;
    }
    if (this.fileTooBig(file)) {
      this.setProgress(this.tooBigMessage(), true);
      return;
    }

    this.uploading = true;
    this.setBusy(true);
    try {
      const url = await this.uploadFile(file);
      if (token !== this.uploadToken) return; // a newer file was chosen meanwhile
      this.setFileFields(file.name, url);
      this.setProgress(`Design uploaded: ${file.name}`);
    } catch (error) {
      if (token !== this.uploadToken) return;
      console.error('kp-quote: upload failed', error);
      if (input) input.value = '';
      if (this.refs.choiceFileRow) this.refs.choiceFileRow.hidden = true;
      const reason = error instanceof Error && error.message ? ` (${error.message})` : '';
      this.setProgress(
        `Your design could not be uploaded${reason}. Please try again, or send the form without it and email your design to us.`,
        true
      );
    } finally {
      if (token === this.uploadToken) {
        this.uploading = false;
        this.setBusy(false);
      }
    }
  }

  /**
   * @param {string} name
   * @param {string} url
   */
  setFileFields(name, url) {
    this.uploadedFile = url ? { name, url } : null;
    const field = this.refs.requestForm?.querySelector('[data-estimate="file"]');
    if (field instanceof HTMLInputElement) field.value = url ? `${name} | ${url}` : '';
  }

  fileTooBig(file) {
    const maxMb = Number(this.refs.fileInput?.dataset.maxMb || 10);
    return file.size > maxMb * 1024 * 1024;
  }

  tooBigMessage() {
    const maxMb = this.refs.fileInput?.dataset.maxMb || 10;
    return `That file is over ${maxMb} MB. Choose a smaller file, or send the form without it and email your design to us.`;
  }

  /** @param {string} message @param {boolean} [isError] */
  setProgress(message, isError = false) {
    if (!this.refs.progress) return;
    this.refs.progress.textContent = message;
    this.refs.progress.classList.toggle('is-error', Boolean(isError && message));
  }

  /** Copies the current estimate and choices into the form's hidden fields */
  fillEstimateFields() {
    const form = this.refs.requestForm;
    if (!form) return;
    const garment = this.garment?.label || this.state.garment;
    const values = {
      method: this.method.label,
      layout: this.state.layout,
      garment,
      item_details: this.itemDetails || (this.garment?.quote_only ? 'Not given' : ''),
      quantity: this.quantityText,
      sizes: this.sizesText.replace(/\n/g, ' | ') || 'Not given',
      unit: this.quote ? this.money(this.quote.unit) : 'Not priced online',
      subtotal: this.quote ? this.money(this.quote.subtotal) : '',
      hst: this.quote ? this.money(this.quote.hst) : '',
      total: this.quote ? this.money(this.quote.total) : '',
      shipping: this.quote ? this.shippingLabel || '' : 'Added to quote',
    };
    for (const [key, value] of Object.entries(values)) {
      const input = form.querySelector(`[data-estimate="${key}"]`);
      if (input instanceof HTMLInputElement) input.value = value;
    }
  }

  /**
   * Runs before the form is sent. Everything here is instant: the design was uploaded when it was
   * chosen, so the form can go straight on to Shopify (including its spam check).
   * @param {SubmitEvent} event
   */
  onRequestSubmit(event) {
    const form = this.refs.requestForm;
    if (!form) return;

    if (this.uploading) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.setProgress('Your design is still uploading. Please wait a moment, then send again.', true);
      return;
    }

    const file = this.refs.fileInput?.files?.[0];
    if (file && this.fileTooBig(file)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.setProgress(this.tooBigMessage(), true);
      this.refs.fileInput?.focus();
      return;
    }

    // Quote by text: Shopify still needs an email, so send the store's own (the email says to text)
    const emailField = form.querySelector('[name="contact[email]"]');
    if (this.replyByText && emailField instanceof HTMLInputElement && !emailField.value.trim()) {
      emailField.value = emailField.dataset.fallbackEmail || '';
    }

    this.fillEstimateFields();
    const fileField = form.querySelector('[data-estimate="file"]');
    if (fileField instanceof HTMLInputElement && !fileField.value) fileField.value = 'No file uploaded';
    this.addSummaryToMessage();
    this.rememberReply();
    this.showSending();
  }

  /**
   * Shows right away that the request is on its way: Shopify runs a spam check before the page
   * changes, which can take a few seconds.
   */
  showSending() {
    const button = this.refs.submit;
    if (button) {
      this.submitLabel ??= button.textContent;
      button.textContent = 'Sending…';
    }
    this.setBusy(true);
    this.setProgress('Sending your quote request…');
    clearTimeout(this.sendTimer);
    this.sendTimer = setTimeout(() => {
      const phone = this.parentElement?.querySelector('[data-kp-thanks] a[href^="tel:"]')?.textContent;
      this.setProgress(
        `Still sending. If a check box appears, complete it to finish.${phone ? ` Problems? Text us at ${phone}.` : ''}`
      );
      this.resetSending(false);
    }, 15000);
  }

  /** @param {boolean} [clearMessage] */
  resetSending(clearMessage = true) {
    clearTimeout(this.sendTimer);
    if (this.refs.submit && this.submitLabel) this.refs.submit.textContent = this.submitLabel;
    this.setBusy(false);
    if (clearMessage) this.setProgress('');
  }

  /** Saves how the customer wants their quote, for the thank-you screen after the page reloads */
  rememberReply() {
    const form = this.refs.requestForm;
    const byText = this.replyByText;
    const value = form?.querySelector(byText ? '[name="contact[phone]"]' : '[name="contact[email]"]')?.value.trim();
    try {
      sessionStorage.setItem('kp-quote-reply', JSON.stringify({ byText, value: value || '' }));
    } catch {
      // Storage blocked: the thank-you screen just leaves this line out
    }
  }

  /** On the thank-you screen: "We'll text your quote to …" or "We'll email your quote to …" */
  showReply(thanks) {
    const line = thanks.querySelector('[data-kp-thanks-reply]');
    if (!(line instanceof HTMLElement)) return;
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem('kp-quote-reply') || 'null');
      sessionStorage.removeItem('kp-quote-reply');
    } catch {
      saved = null;
    }
    if (!saved?.value) return;
    line.textContent = saved.byText ? `We'll text your quote to ${saved.value}.` : `We'll email your quote to ${saved.value}.`;
    line.hidden = false;
  }

  /**
   * Also writes the estimate and design link into the main message, which Shopify always shows
   * at the top of the email, so the link can't be missed.
   */
  addSummaryToMessage() {
    const form = this.refs.requestForm;
    const body = form?.querySelector('[name="contact[body]"]');
    if (!(body instanceof HTMLTextAreaElement)) return;
    // Backup copy of the link without "https://", in case a mail filter ever strips full links
    const fileValue = this.uploadedFile
      ? `${this.uploadedFile.name} | ${this.uploadedFile.url.replace(/^https?:\/\//, '')}`
      : 'No file uploaded';
    const ownText = body.value.split('--- Quote request ---')[0].trim();
    const lines = [
      ownText,
      '',
      '--- Quote request ---',
      this.replyByText
        ? `Reply by: TEXT to ${form.querySelector('[name="contact[phone]"]')?.value || 'no number given'}`
        : 'Reply by: email',
      this.summary,
      `Design file: ${fileValue}`,
    ];
    body.value = lines.join('\n').trim();
  }

  /** @param {boolean} busy */
  setBusy(busy) {
    this.refs.submit?.setAttribute('aria-busy', String(busy));
  }

  /**
   * Uploads the design and returns its link: through Shopify when a design upload product is set,
   * otherwise through Cloudinary.
   * @param {File} file
   * @returns {Promise<string>}
   */
  uploadFile(file) {
    if (this.refs.fileInput?.dataset.uploadVariant) return this.uploadToShopify(file);
    return this.uploadToCloudinary(file);
  }

  /**
   * Uploads through Shopify's cart: the file is attached as a hidden line item property on the
   * $0 "Design upload" product, Shopify stores it and returns its link, then the line is removed
   * so the customer's cart is left as it was.
   * @param {File} file
   * @returns {Promise<string>} The file's link
   */
  async uploadToShopify(file) {
    const variantId = this.refs.fileInput?.dataset.uploadVariant;
    const root = window.Shopify?.routes?.root || '/';
    const body = new FormData();
    body.append('id', String(variantId));
    body.append('quantity', '1');
    body.append('properties[_Design file]', file);

    const item = await new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open('POST', `${root}cart/add.js`);
      request.setRequestHeader('Accept', 'application/json');
      request.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          this.setProgress(`Uploading your design… ${Math.round((event.loaded / event.total) * 100)}%`);
        }
      });
      request.addEventListener('load', () => {
        try {
          const response = JSON.parse(request.responseText);
          if (request.status >= 200 && request.status < 300) resolve(response);
          else reject(new Error(response.description || response.message || `Upload failed (${request.status})`));
        } catch (error) {
          reject(error);
        }
      });
      request.addEventListener('error', () => reject(new Error('Network error')));
      this.setProgress('Uploading your design… 0%');
      request.send(body);
    });

    // Some responses wrap the line in "items"
    const line = Array.isArray(item?.items) ? item.items[0] : item;
    const url = line?.properties?.['_Design file'];

    if (line?.key) {
      try {
        await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: 0 }),
        });
      } catch (error) {
        console.warn('kp-quote: could not remove the upload line from the cart', error);
      }
    }

    if (typeof url !== 'string' || !url.startsWith('http')) {
      throw new Error('Shopify did not return a file link');
    }
    return url;
  }

  /**
   * Uploads straight to Cloudinary with the merchant's unsigned preset.
   * @param {File} file
   * @returns {Promise<string>} The file's secure URL
   */
  uploadToCloudinary(file) {
    const input = this.refs.fileInput;
    const cloud = input?.dataset.cloud;
    const preset = input?.dataset.preset;
    if (!cloud || !preset) return Promise.reject(new Error('Uploads are not set up'));

    return new Promise((resolve, reject) => {
      const body = new FormData();
      body.append('file', file);
      body.append('upload_preset', preset);

      const request = new XMLHttpRequest();
      request.open('POST', `https://api.cloudinary.com/v1_1/${encodeURIComponent(cloud)}/auto/upload`);
      request.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          this.setProgress(`Uploading your design… ${Math.round((event.loaded / event.total) * 100)}%`);
        }
      });
      request.addEventListener('load', () => {
        try {
          const response = JSON.parse(request.responseText);
          if (request.status >= 200 && request.status < 300 && response.secure_url) {
            resolve(response.secure_url);
          } else {
            reject(new Error(response.error?.message || `Upload failed (${request.status})`));
          }
        } catch (error) {
          reject(error);
        }
      });
      request.addEventListener('error', () => reject(new Error('Network error')));
      this.setProgress('Uploading your design… 0%');
      request.send(body);
    });
  }

  /**
   * Copies the estimate so it can be pasted into the request form. The form is rendered by the
   * Hulk Form Builder app inside a cross-origin iframe, so it can't be filled in automatically.
   */
  async copySummary() {
    const summary = this.summary;
    if (!summary) return;
    let message = 'Copied. Paste it into the message box in step 05.';
    try {
      await navigator.clipboard.writeText(summary);
    } catch {
      message = 'Copy is blocked in this browser. Please type your estimate into the form.';
    }
    if (this.refs.copyStatus) this.refs.copyStatus.textContent = message;
    if (this.refs.barCopy) this.refs.barCopy.textContent = 'Copied';
    clearTimeout(this.copyTimer);
    this.copyTimer = setTimeout(() => {
      if (this.refs.copyStatus) this.refs.copyStatus.textContent = '';
      if (this.refs.barCopy) this.refs.barCopy.textContent = 'Copy';
    }, 6000);
  }
}

if (!customElements.get('kp-quote')) {
  customElements.define('kp-quote', KpQuote);
}
